'use client';

import { analytics } from '@/lib/posthog/events';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'motion/react';
import Image from 'next/image';

// The team members who run onboarding demos. Order matters: rendered left to right
// with overlap, so the last entry (Ole August) sits on top and is most visible.
const TEAM_AVATARS = [
  { src: '/images/team-no-bg/bengt-no-bg.png', alt: 'Bengt, Balder team' },
  { src: '/images/team-no-bg/anders-no-bg.png', alt: 'Anders, Balder team' },
  { src: '/images/team-no-bg/hartvik-no-bg.png', alt: 'Hartvik, Balder team' },
  { src: '/images/team-no-bg/oleaugust-no-bg.png', alt: 'Ole August, Balder team' },
];

// Demo card per the approved Claude Design (Sign-in.dc.html, 6a/6m): white card,
// team avatar cluster, full-width lime CTA. ~99% of /sign-in visitors have no
// account (self-signup is restricted), so this is the page's primary action.
export function DemoCard({ source }: { source: 'signIn' | 'signUp' }) {
  return (
    <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,.22)]">
      <div className="flex items-start justify-between gap-3">
        <p className="font-heading text-xl leading-tight text-foreground">
          Don&apos;t have an account?
        </p>
        <div className="flex shrink-0">
          {/* Fast stagger, left to right — Ole August (last) pops in last */}
          {TEAM_AVATARS.map((avatar, index) => (
            <motion.div
              key={avatar.src}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.07, duration: 0.2, ease: 'easeOut' }}
              className={`bg-balder-lime size-[34px] overflow-hidden rounded-full border-2 border-white ring-[0.5px] ring-ring ${index > 0 ? '-ml-2.5' : ''}`}
            >
              {/* Fixed dimensions so next/image serves a tiny optimized variant, not the ~1MB
                  source. mt-1 gives the head some air below the circle's ceiling. Faces fade
                  in over the lime circle instead of popping in when the image loads. */}
              <Image
                src={avatar.src}
                alt={avatar.alt}
                width={34}
                height={34}
                className="mt-1 size-full object-cover object-top opacity-0 transition-opacity duration-300"
                onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
              />
            </motion.div>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        Balder Cloud is for container fleets with Northstar devices installed. We&apos;re actively
        onboarding new fleets. Book a short demo to see how this would look on your own containers.
      </p>
      {/* New tab keeps the auth page alive — and gives PostHog time to flush the event */}
      <a
        href="https://balder.co/contact"
        target="_blank"
        rel="noopener"
        onClick={() => analytics.demoCardClicked({ source })}
        className="bg-balder-lime text-balder-black mt-4 flex w-full items-center justify-center gap-2 rounded-[10px] py-3 text-sm font-semibold"
      >
        Book a demo
        <ArrowUpRight className="size-4" aria-hidden />
      </a>
    </div>
  );
}
